/**
 * BIRHAUS Education Theme
 * 
 * Specialized theme for educational systems, student management, and learning platforms.
 * 
 * Design principles:
 * - Warm and encouraging colors for learning environments
 * - Clear visual hierarchy for educational content
 * - Accessible design for diverse learning needs
 * - Spanish-first for Latin American educational contexts
 */

import { BirhausThemeConfig } from './financial'

export const educationTheme: BirhausThemeConfig = {
  name: 'BIRHAUS Education',
  version: '3.0.0',
  description: 'v3.0 Radical Minimalist warm theme for educational systems, student management, and learning platforms',
  industry: 'education',
  
  colors: {
    // Primary: Warm orange for creativity and learning
    primary: '#EA580C',
    primaryHover: '#C2410C',
    primaryDisabled: '#FED7AA',
    
    // Semantic colors for educational contexts
    success: '#16A34A',
    successBackground: '#F0FDF4',
    warning: '#CA8A04',
    warningBackground: '#FEFCE8',
    error: '#DC2626',
    errorBackground: '#FEF2F2',
    info: '#2563EB',
    infoBackground: '#EFF6FF',
    
    // Warm neutrals for learning comfort
    background: '#FFFBF7',
    surface: '#FFF7ED',
    surfaceElevated: '#FFFFFF',
    border: '#FED7AA',
    borderSubtle: '#FFEDD5',
    
    // Text colors optimized for readability
    textPrimary: '#9A3412',
    textSecondary: '#C2410C',
    textDisabled: '#FED7AA',
    textInverse: '#FFFFFF',
    
    // Education-specific colors
    income: '#16A34A',     // Green for achievements
    expense: '#DC2626',    // Red for warnings
    transfer: '#9333EA',   // Purple for assignments
    pending: '#CA8A04',    // Yellow for pending review
    approved: '#16A34A',   // Green for approved
    rejected: '#DC2626'    // Red for failed
  },
  
  typography: {
    fontFamily: {
      sans: ['Inter', 'system-ui', '-apple-system', 'sans-serif'],
      mono: ['JetBrains Mono', 'Consolas', 'monospace']
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem'
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.6,
      relaxed: 1.75
    }
  },
  
  spacing: {
    '0': '0',
    '1': '0.25rem',
    '2': '0.5rem',
    '3': '0.75rem',
    '4': '1rem',
    '5': '1.25rem',
    '6': '1.5rem',
    '8': '2rem',
    '10': '2.5rem',
    '12': '3rem',
    '16': '4rem',
    '20': '5rem',
    '24': '6rem',
    '32': '8rem'
  },
  
  borderRadius: {
    none: '0',
    sm: '0.25rem',
    base: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px'
  },
  
  boxShadow: {
    sm: '0 1px 2px 0 rgb(234 88 12 / 0.05)',
    base: '0 1px 3px 0 rgb(234 88 12 / 0.1), 0 1px 2px -1px rgb(234 88 12 / 0.1)',
    lg: '0 10px 15px -3px rgb(234 88 12 / 0.1), 0 4px 6px -4px rgb(234 88 12 / 0.1)',
    xl: '0 20px 25px -5px rgb(234 88 12 / 0.1), 0 8px 10px -6px rgb(234 88 12 / 0.1)'
  },
  
  // v3.0 Glass Morphism Configuration with warm education colors
  glass: {
    primary: 'rgba(234, 88, 12, 0.15)',
    secondary: 'rgba(234, 88, 12, 0.08)',
    surface: 'rgba(255, 247, 237, 0.85)',
    border: 'rgba(254, 215, 170, 0.3)',
    blurIntensity: {
      light: 8,
      medium: 12,
      heavy: 24
    },
    saturation: 140,
    opacity: {
      subtle: 0.1,
      light: 0.25,
      medium: 0.45,
      heavy: 0.65,
      ultra: 0.8
    }
  },
  
  // v3.0 Generous Spacing (2x traditional)
  spacingV3: {
    none: '0px',
    xs: '8px',
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '48px',
    '2xl': '64px',
    '3xl': '96px',
    cardPadding: '32px',
    buttonPadding: '16px 32px',
    inputPadding: '16px 20px',
    sectionMargin: '48px'
  },
  
  // v3.0 Radical Border Radius with rounded education feel
  borderRadiusV3: {
    xs: '10px',
    sm: '14px',
    md: '18px',
    lg: '26px',
    xl: '34px',
    '2xl': '42px',
    '3xl': '50px',
    circle: '50%',
    pill: '9999px',
    card: '26px',
    button: '18px',
    input: '14px',
    dialog: '34px'
  },
  
  // v3.0 Enhanced Shadows with Warm Educational Effects
  shadowV3: {
    none: 'none',
    sm: '0 1px 3px rgba(234, 88, 12, 0.08)',
    md: '0 4px 12px rgba(234, 88, 12, 0.08)',
    lg: '0 8px 24px rgba(234, 88, 12, 0.1)',
    xl: '0 16px 48px rgba(234, 88, 12, 0.12)',
    glass: '0 8px 32px rgba(234, 88, 12, 0.06)',
    floating: '0 12px 40px rgba(234, 88, 12, 0.15)',
    elevated: '0 20px 60px rgba(234, 88, 12, 0.18)',
    button: '0 4px 12px rgba(234, 88, 12, 0.1)',
    card: '0 8px 24px rgba(234, 88, 12, 0.08)',
    dialog: '0 24px 80px rgba(234, 88, 12, 0.2)',
    tooltip: '0 4px 16px rgba(234, 88, 12, 0.12)'
  },
  
  // v3.0 Gentle Animations for Learning Context
  animationV3: {
    duration: {
      instant: '0ms',
      fast: '200ms',
      base: '350ms',
      slow: '550ms',
      slower: '800ms',
      slowest: '1100ms'
    },
    easing: {
      linear: 'linear',
      smooth: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
      snappy: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      gentle: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
      entrance: 'cubic-bezier(0, 0, 0.2, 1)',
      exit: 'cubic-bezier(0.4, 0, 1, 1)'
    },
    useGPU: true,
    respectReducedMotion: true
  },
  
  components: {
    currency: {
      positive: '#16A34A',
      negative: '#DC2626',
      neutral: '#9A3412',
      fontSize: '1rem',
      fontFamily: 'JetBrains Mono, monospace',
      fontWeight: 600
    },
    
    statusBadge: {
      pending: { bg: '#FEFCE8', text: '#A16207', border: '#EAB308' },
      approved: { bg: '#F0FDF4', text: '#15803D', border: '#22C55E' },
      rejected: { bg: '#FEF2F2', text: '#991B1B', border: '#F87171' },
      transferred: { bg: '#F3F4F6', text: '#374151', border: '#9CA3AF' },
      received: { bg: '#FFF7ED', text: '#C2410C', border: '#FB923C' }
    },
    
    dataTable: {
      headerBg: '#FFF7ED',
      headerText: '#9A3412',
      rowBg: '#FFFFFF',
      rowBgAlt: '#FFFBF7',
      rowBorder: '#FFEDD5',
      textAlign: 'left'
    },
    
    form: {
      inputBg: '#FFFFFF',
      inputBorder: '#FED7AA',
      inputFocusBorder: '#EA580C',
      labelText: '#9A3412',
      errorText: '#DC2626',
      successText: '#16A34A'
    },
    
    card: {
      bg: '#FFFFFF',
      border: '#FED7AA',
      shadow: '0 1px 3px 0 rgb(234 88 12 / 0.1)',
      headerBg: '#FFF7ED',
      padding: '1.5rem'
    }
  },
  
  labels: {
    es: {
      amount: 'Cantidad',
      balance: 'Balance',
      income: 'Ingreso',
      expense: 'Gasto',
      transfer: 'Transferencia',
      date: 'Fecha',
      status: 'Estado',
      pending: 'Pendiente',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      total: 'Total',
      subtotal: 'Subtotal',
      tax: 'Impuesto',
      
      save: 'Guardar',
      cancel: 'Cancelar',
      edit: 'Editar',
      delete: 'Eliminar',
      approve: 'Aprobar',
      reject: 'Rechazar',
      transferAction: 'Transferir',
      export: 'Exportar',
      print: 'Imprimir',
      
      dashboard: 'Tablero',
      transactions: 'Transacciones',
      reports: 'Reportes',
      settings: 'Configuración',
      users: 'Usuarios',
      
      success: 'Éxito',
      error: 'Error',
      warning: 'Advertencia',
      loading: 'Cargando...',
      noData: 'No hay datos disponibles'
    }
  }
}

export default educationTheme